'use client';

import Image from 'next/image';

import { cn } from '@/libs';
import { RaffleStatus } from '@/types/enums';
import type { Raffle } from '@/types/models/Raffle';
import {
  formatCurrency,
  isRaffleOnSale,
  isRaffleUpcoming,
  UPCOMING_SALE_LABEL,
} from '@/utils';

import { useRaffleCard } from './hooks';

import { Badge, Button, Icon } from '../ui';
import { formatDateTime } from '../ui/InputForm/utils/formatDate';

type RaffleCardProps = {
  raffle: Raffle;
};

export const RaffleCard = ({ raffle }: RaffleCardProps) => {
  const { handleBuyClick, handleDetailsClick } = useRaffleCard({ raffle });
  const isActive = isRaffleOnSale(raffle.status);
  const isUpcoming = isRaffleUpcoming(raffle.status);
  const isDisabled = !isActive;

  return (
    <div
      data-cy={`raffle-card-${raffle.id}`}
      className="w-full flex flex-row gap-3 bg-background-variant rounded-lg p-3 overflow-hidden"
      key={raffle.id}
    >
      <Image
        src={raffle.urlThumbnail}
        alt={raffle.name}
        width={100}
        height={100}
        priority
        unoptimized
        className="object-cover rounded-lg w-24 h-full"
      />
      <div className="w-full flex flex-col flex-1 gap-2">
        <div className="w-full flex flex-row items-start justify-between gap-1">
          <h1
            data-cy={`raffle-card-${raffle.id}-name`}
            className="text-foreground text-sm font-bold flex-1 line-clamp-1"
          >
            {raffle.name}
          </h1>
          <Badge
            text={`Edição nº:${raffle.numbering}`}
            className={cn('bg-secondary-variant', isDisabled && 'bg-foreground/10')}
            cnText="text-foreground text-xxs"
          />
        </div>

        <div className="w-full flex items-center justify-center py-2 border-2 border-foreground/10 rounded-lg">
          <span
            data-cy={`raffle-card-${raffle.id}-draw-date`}
            className="text-xs text-foreground"
          >
            Sorteio: {formatDateTime(raffle.drawDate)}
          </span>
        </div>

        {raffle.status !== RaffleStatus.Enum.FINISHED && (
          <div className="w-full flex items-center justify-center py-2 border-2 border-foreground/10 rounded-lg">
            <span
              data-cy={`raffle-card-${raffle.id}-ticket-price`}
              className="text-xs text-foreground"
            >
              Adquira por: {formatCurrency(raffle.ticketPrice)}
            </span>
          </div>
        )}

        {raffle.status === RaffleStatus.Enum.FINISHED ? (
          <Button
            data-cy={`raffle-card-${raffle.id}-details-button`}
            type="button"
            onClick={handleDetailsClick}
            loadingText="Acessando..."
            variant="primary"
            className={cn(
              'h-8 w-full flex items-center text-background text-sm uppercase mt-2',
            )}
          >
            <span className="mx-auto">Ver detalhes</span>
            <Icon.ChevronRight width={20} height={20} />
          </Button>
        ) : (
          <Button
            data-cy={`raffle-card-${raffle.id}-buy-button`}
            type="button"
            onClick={handleBuyClick}
            disabled={isDisabled}
            loadingText="Acessando..."
            variant="primary"
            className={cn(
              'h-8 w-full flex items-center text-background text-sm uppercase mt-2',
              isDisabled && 'bg-foreground/10 cursor-not-allowed',
            )}
          >
            <span className="mx-auto">
              {isActive
                ? 'Comprar'
                : isUpcoming
                  ? UPCOMING_SALE_LABEL
                  : 'Vendas Encerradas'}
            </span>
            <Icon.ChevronRight width={20} height={20} />
          </Button>
        )}
      </div>
    </div>
  );
};
